import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import ItemRow from "./ItemRow";
import ProgressBar from "./ProgressBar";
import StatusBadge from "./StatusBadge";
import useCrateData from "../hooks/useCrateData";
import usePackUnpack from "../hooks/usePackUnpack";
import useMarking from "../hooks/useMarking";
import { tokens } from "../styles";
import { packingTripLabelsFromItems } from "../utils";

// Per-crate accordion inside PackingInterfaceScreen — mirrors the
// prototype's collapsible card-per-crate layout. Lazy-fetches its items
// only when expanded so a DB with many crates doesn't fan out N parallel
// /get-crate-cx-items requests on mount.
//
// Props:
//   summaryCrate  → the crate row from data.CRATE (id, locality, packed_tps,
//                   tp_count, is_rescheduled, …)
//   onPressMark   → opens the MarkingSheet (parent owns the modal)
//   onPressUnpack → opens the UnpackConfirmSheet (parent owns the modal)

const DbCrateAccordion = ({
  summaryCrate,
  tab = "today",
  onPressMark,
  setUnpackTarget,
  parentReload,
}) => {
  const crateId = summaryCrate?.crate_id;
  const [open, setOpen] = useState(false);
  const {
    data: crateData,
    loading,
    reload,
  } = useCrateData({
    crateId: open ? crateId : "",
    tab,
  });
  // Combined reload: refreshes BOTH the per-crate items endpoint (so the
  // accordion's items / Pack-Visible button update locally) AND the
  // parent's dbs-snapshot (so summaryCrate.packed_tps / packed_pill /
  // overall progress update at the screen level). Without the parent
  // reload, packing a crate updates the items list but the collapsed
  // header still shows 0/N and no PACKED pill until the user navigates
  // away and back.
  const combinedReload = useCallback(async () => {
    await reload();
    if (parentReload) await parentReload();
  }, [reload, parentReload]);
  const { pack, packing, unpacking } = usePackUnpack({
    reload: combinedReload,
  });
  const { isProcessing } = useMarking({ reload: combinedReload });

  // Auto-fetch on first open.
  useEffect(() => {
    if (open && !crateData && !loading) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Refresh items when the parent's summary numbers change. After a pack
  // or unpack the parent's reload() refetches dbs-snapshot, which updates
  // summaryCrate.packed_tps. Without this effect the accordion's local
  // crateData stays stale until the user collapses + reopens, leaving
  // "Pack Visible Items" / "Unpack" buttons + items in the wrong state.
  const summaryKey = `${summaryCrate?.packed_tps ?? 0}-${summaryCrate?.tp_count ?? 0}`;
  useEffect(() => {
    if (open && crateId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryKey]);

  // Match the section whose crate_id === this accordion's crateId.
  // /get-crate-cx-items returns crate_data as an array of sections —
  // currentCrateData first, then one per connecting crate. Flattening
  // across all sections leaked connecting-crate items into the current
  // crate's accordion (e.g. CP-00366717's 2 items rendered as 2/31, the
  // extra 29 from connecting CP-00320020). DB-card flow only lists the
  // current crate's items here; connecting crates render in the read-
  // only "CONNECTING CRATES" section in PackingInterfaceScreen.
  const items = useMemo(() => {
    const sections = Array.isArray(crateData?.crate_data)
      ? crateData.crate_data
      : [];
    const currentSection = sections.find(
      (s) => String(s?.crate_id) === String(crateId),
    );
    return Array.isArray(currentSection?.items) ? currentSection.items : [];
  }, [crateData, crateId]);

  const totalQty = items.reduce((s, it) => s + Number(it?.quantity || 0), 0);
  const tripLabels = useMemo(() => packingTripLabelsFromItems(items), [items]);
  const packedQty = items
    .filter((it) => it?.packed_at)
    .reduce((s, it) => s + Number(it?.quantity || 0), 0);
  const summaryPercent = totalQty
    ? Math.round((packedQty / totalQty) * 100)
    : Number(summaryCrate?.tp_count) > 0
      ? Math.round(
          (Number(summaryCrate?.packed_tps || 0) /
            Number(summaryCrate?.tp_count || 1)) *
            100,
        )
      : 0;

  const sectionForUnpack = useMemo(() => {
    const sections = Array.isArray(crateData?.crate_data)
      ? crateData.crate_data
      : [];
    return (
      sections.find((s) => String(s?.crate_id) === String(crateId)) || null
    );
  }, [crateData, crateId]);

  const handlePack = useCallback(() => {
    const target = sectionForUnpack || {
      crate_id: crateId,
      title: summaryCrate?.title,
      items,
    };
    pack(target);
  }, [pack, sectionForUnpack, crateId, summaryCrate, items]);

  const handleUnpack = useCallback(() => {
    if (!setUnpackTarget) return;
    const target = sectionForUnpack || {
      crate_id: crateId,
      title: summaryCrate?.title,
      items,
    };
    setUnpackTarget(target);
  }, [setUnpackTarget, sectionForUnpack, crateId, summaryCrate, items]);

  const hasUnpacked = items.some((it) => !it?.packed_at && it?.order_item_id);
  const hasPacked = items.some(
    (it) => Boolean(it?.packed_at) && it?.order_item_id,
  );

  return (
    <View style={styles.card}>
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => setOpen((v) => !v)}
        style={styles.headerRow}
      >
        <View style={styles.headerLeft}>
          <Text variant="title_16" style={styles.title} numberOfLines={1}>
            {summaryCrate?.crate_id || "—"}
          </Text>
          <Text variant="caption_2" style={styles.locality} numberOfLines={1}>
            {summaryCrate?.title?.split(" - ")?.[1] || ""}
          </Text>
        </View>
        <View style={styles.headerRight}>
          <Text variant="caption_2" style={styles.metric}>
            {Number(summaryCrate?.packed_tps || 0)}/
            {Number(summaryCrate?.tp_count || 0)}
          </Text>
          <View style={[styles.chev, open ? styles.chevUp : styles.chevDown]}>
            <ChevronRight
              width={10}
              height={10}
              pathFill={tokens.textSecondary}
            />
          </View>
        </View>
      </TouchableOpacity>
      <View style={styles.metaRow}>
        {Number(summaryCrate?.tp_count || 0) > 0 &&
        Number(summaryCrate?.packed_tps || 0) >=
          Number(summaryCrate?.tp_count || 0) ? (
          <StatusBadge label="PACKED" state="packed" />
        ) : null}
        {summaryCrate?.is_rescheduled ? (
          <StatusBadge label="Rescheduled" state="rescheduled" />
        ) : null}
        {tripLabels.map((label) => (
          <StatusBadge key={label} label={label} state="ofd" />
        ))}
        {summaryCrate?.crate_size ? (
          <StatusBadge label={summaryCrate.crate_size} state="neutral" />
        ) : null}
      </View>
      {open ? (
        <View style={styles.body}>
          {loading && !crateData ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator color={tokens.textSecondary} />
              <Text variant="caption_2" style={styles.loadingText}>
                Loading items…
              </Text>
            </View>
          ) : items.length === 0 ? (
            <Text variant="caption_2" style={styles.empty}>
              No items in this crate.
            </Text>
          ) : (
            <>
              <View style={styles.progressWrap}>
                <ProgressBar percent={summaryPercent} height={6} />
                <Text variant="caption_2" style={styles.progressText}>
                  {packedQty}/{totalQty} items packed
                </Text>
              </View>
              <View style={styles.actionsRow}>
                {hasUnpacked ? (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    disabled={packing}
                    onPress={handlePack}
                    style={[
                      styles.actionBtn,
                      styles.actionPrimary,
                      packing && styles.actionDisabled,
                    ]}
                  >
                    <Text variant="caption_2" style={styles.actionPrimaryText}>
                      Pack Visible Items
                    </Text>
                  </TouchableOpacity>
                ) : null}
                {hasPacked ? (
                  <TouchableOpacity
                    activeOpacity={0.85}
                    disabled={unpacking}
                    onPress={handleUnpack}
                    style={[
                      styles.actionBtn,
                      styles.actionDanger,
                      unpacking && styles.actionDisabled,
                    ]}
                  >
                    <Text variant="caption_2" style={styles.actionDangerText}>
                      Unpack
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
              <View style={styles.itemsList}>
                {items.map((it) => (
                  <ItemRow
                    key={String(it?.order_item_id || it?.hash || Math.random())}
                    item={it}
                    processing={isProcessing(it?.order_item_id)}
                    onPressMark={(item) => onPressMark?.(item)}
                  />
                ))}
              </View>
            </>
          )}
        </View>
      ) : null}
    </View>
  );
};

export default DbCrateAccordion;

const styles = StyleSheet.create({
  card: {
    backgroundColor: tokens.surface,
    borderRadius: tokens.radius.lg,
    paddingVertical: tokens.spacing.medium,
    paddingHorizontal: tokens.spacing.large,
    marginHorizontal: tokens.spacing.page,
    marginBottom: tokens.spacing.small,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: tokens.borderLight,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerLeft: { flex: 1, marginRight: tokens.spacing.small },
  title: { color: tokens.textPrimary, fontWeight: "700", fontSize: 14 },
  locality: { color: tokens.textSecondary, fontSize: 11, marginTop: 2 },
  headerRight: { flexDirection: "row", alignItems: "center" },
  metric: {
    color: tokens.textPrimary,
    fontSize: 13,
    fontWeight: "700",
    marginRight: 8,
  },
  chev: {
    width: 12,
    height: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  chevDown: { transform: [{ rotate: "90deg" }] },
  chevUp: { transform: [{ rotate: "-90deg" }] },
  metaRow: { flexDirection: "row", flexWrap: "wrap", marginTop: 6 },
  body: {
    marginTop: tokens.spacing.medium,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: tokens.borderLight,
    paddingTop: tokens.spacing.medium,
  },
  loadingWrap: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: tokens.spacing.medium,
  },
  loadingText: {
    color: tokens.textSecondary,
    marginLeft: 8,
    fontSize: 12,
  },
  empty: {
    color: tokens.textTertiary,
    fontSize: 12,
    textAlign: "center",
    paddingVertical: tokens.spacing.medium,
  },
  progressWrap: { marginBottom: tokens.spacing.medium },
  progressText: {
    color: tokens.textSecondary,
    fontSize: 11,
    marginTop: 4,
  },
  actionsRow: { flexDirection: "row", marginBottom: tokens.spacing.medium },
  actionBtn: {
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: 8,
    borderRadius: tokens.radius.md,
    marginRight: tokens.spacing.small,
  },
  actionPrimary: { backgroundColor: tokens.primary.bg },
  actionPrimaryText: {
    color: tokens.primary.fg,
    fontSize: 12,
    fontWeight: "700",
  },
  actionDanger: { backgroundColor: tokens.error },
  actionDangerText: { color: tokens.surface, fontSize: 12, fontWeight: "700" },
  actionDisabled: { opacity: 0.5 },
  itemsList: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: tokens.borderLight,
  },
});
